// Map.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Autocomplete,
  DirectionsRenderer,
  Marker,
} from "@react-google-maps/api";
import "./LightMap.css";
import { Link } from "react-router-dom";
import QuizPrompt from "./QuizPrompt";
import StreetLightIcon from "../pictures/light.png";

const LoadingOverlay = () => (
  <div className="loading-overlay">
    <div className="loading-spinner"></div>
    <p>Loading...</p>
  </div>
);

const center = {
  lat: -37.8136,
  lng: 144.9631,
};

const libraries = ["places", "geometry"];

function Map() {
  const [map, setMap] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [markersData, setMarkersData] = useState([]);

  const [directionsResponse, setDirectionsResponse] = useState(null);

  const [pinMode, setPinMode] = useState(false);
  const [hintMessage, setHintMessage] = useState("");

  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [lastRoute, setLastRoute] = useState(null);

  const [selectedWaypoint, setSelectedWaypoint] = useState(null);

  const [showAllStreetlights, setShowAllStreetlights] = useState(false);

  const [clickMode, setClickMode] = useState(null);

  const [detourPreference, setDetourPreference] = useState("Distance First");

  const [routeInfo, setRouteInfo] = useState({ distance: "", duration: "" });

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBmXc5jBIHoFm5Qdwf88B1ApGGpR_kCSZo",
    libraries: libraries,
  });

  const geocoder = useRef(null);

  const originAutocompleteRef = useRef(null);
  const destinationAutocompleteRef = useRef(null);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    geocoder.current = new window.google.maps.Geocoder();
  }, []);

  const [showQuizPrompt, setShowQuizPrompt] = useState(false);

  // Show quiz prompt after 1 seconds of route calculation
  useEffect(() => {
    if (directionsResponse && routeInfo.distance && routeInfo.duration) {
      const timer = setTimeout(() => setShowQuizPrompt(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [directionsResponse, routeInfo]);

  // Fetch streetlight data but do not display markers initially
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://nxs4mvj68d.execute-api.ap-southeast-2.amazonaws.com/prod/lightdata"
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        const data = JSON.parse(responseData.body);

        if (Array.isArray(data)) {
          setMarkersData(data);
        } else {
          console.error("Unexpected API response format. Expected an array.");
        }

        setIsDataLoaded(true);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsDataLoaded(true);
      }
    };

    fetchData();
  }, []);

  // Recalculate route when origin, destination, or detour preference changes
  useEffect(() => {
    if (origin && destination) {
      setSelectedWaypoint(null);
      calculateRoute();
    }
  }, [detourPreference]);

  // Reverse geocode function
  const reverseGeocode = (latLng, callback) => {
    geocoder.current.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        callback(results[0].formatted_address);
      } else {
        console.error("Geocoder failed due to: " + status);
        callback("");
      }
    });
  };

  // Handle map clicks for setting origin or destination
  const onMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const latLng = { lat, lng };

    if (clickMode === "origin") {
      reverseGeocode(latLng, (address) => {
        setOrigin({
          address,
          location: latLng,
        });
      });
      setClickMode(null);
      setPinMode(false);
      setHintMessage("");
    } else if (clickMode === "destination") {
      reverseGeocode(latLng, (address) => {
        setDestination({
          address,
          location: latLng,
        });
      });
      setClickMode(null);
      setPinMode(false);
      setHintMessage("");
    }
  };

  // Function to use current location as origin
  const useCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          reverseGeocode(latLng, (address) => {
            setOrigin({
              address,
              location: latLng,
            });
            map?.panTo(latLng);
            map?.setZoom(14);
          });
        },
        () => {
          alert("Geolocation failed or is not supported by your browser.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  // Handlers for Autocomplete place changes
  const onOriginPlaceChanged = () => {
    if (originAutocompleteRef.current !== null) {
      const place = originAutocompleteRef.current.getPlace();
      if (place.geometry) {
        const location = place.geometry.location;
        setOrigin({
          address: place.formatted_address,
          location: {
            lat: location.lat(),
            lng: location.lng(),
          },
        });
      } else {
        console.error("No geometry found for origin place");
      }
    }
  };

  const onDestinationPlaceChanged = () => {
    if (destinationAutocompleteRef.current !== null) {
      const place = destinationAutocompleteRef.current.getPlace();
      if (place.geometry) {
        const location = place.geometry.location;
        setDestination({
          address: place.formatted_address,
          location: {
            lat: location.lat(),
            lng: location.lng(),
          },
        });
      } else {
        console.error("No geometry found for destination place");
      }
    }
  };

  // Function to compare two routes based on their overview paths
  const areRoutesEqual = (route1, route2) => {
    if (!route1.overview_path || !route2.overview_path) {
      return false;
    }

    if (route1.overview_path.length !== route2.overview_path.length) {
      return false;
    }

    return route1.overview_path.every((point, index) => {
      return (
        point.lat() === route2.overview_path[index].lat() &&
        point.lng() === route2.overview_path[index].lng()
      );
    });
  };

  // Helper function to compute the distance of a route
  const computeRouteDistance = (route) => {
    if (!route.legs) return 0;
    let totalDistance = 0;
    route.legs.forEach((leg) => {
      totalDistance += leg.distance.value;
    });
    return totalDistance;
  };

  // Helper function to calculate streetlight coverage
  const calculateStreetlightCoverage = (route, streetlights) => {
    if (!route.overview_path) return 0;
    const routePath = route.overview_path;

    const proximityThreshold = 50;

    let totalPoints = routePath.length;
    let pointsWithCoverage = 0;

    routePath.forEach((point) => {
      const pointLatLng = new window.google.maps.LatLng(
        point.lat(),
        point.lng()
      );
      const isNearStreetlight = streetlights.some((streetlight) => {
        const streetlightLatLng = new window.google.maps.LatLng(
          parseFloat(streetlight.latitude),
          parseFloat(streetlight.longitude)
        );
        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            pointLatLng,
            streetlightLatLng
          );
        return distance <= proximityThreshold;
      });
      if (isNearStreetlight) {
        pointsWithCoverage += 1;
      }
    });

    return pointsWithCoverage / totalPoints;
  };

  // Function to select one optimal waypoint based on the logic
  const selectOptimalWaypoint = (origin, destination, streetlights) => {
    if (!window.google || !window.google.maps) return null;

    // Convert origin and destination to LatLng objects
    const originLatLng = new window.google.maps.LatLng(
      origin.location.lat,
      origin.location.lng
    );
    const destinationLatLng = new window.google.maps.LatLng(
      destination.location.lat,
      destination.location.lng
    );

    // Calculate the direct line points: 1/4, middle, 3/4
    const oneQuarterLatLng = window.google.maps.geometry.spherical.interpolate(
      originLatLng,
      destinationLatLng,
      0.25
    );
    const middleLatLng = window.google.maps.geometry.spherical.interpolate(
      originLatLng,
      destinationLatLng,
      0.5
    );
    const threeQuarterLatLng =
      window.google.maps.geometry.spherical.interpolate(
        originLatLng,
        destinationLatLng,
        0.75
      );

    const pointsToCheck = [
      { point: middleLatLng, label: "Middle" },
      { point: oneQuarterLatLng, label: "1/4" },
      { point: threeQuarterLatLng, label: "3/4" },
    ];

    const proximityThreshold = 300;

    for (let i = 0; i < pointsToCheck.length; i++) {
      const currentPoint = pointsToCheck[i].point;

      // Find streetlights within 300 meters of the current point
      const nearbyStreetlights = streetlights.filter((streetlight) => {
        const streetlightLatLng = new window.google.maps.LatLng(
          parseFloat(streetlight.latitude),
          parseFloat(streetlight.longitude)
        );
        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            currentPoint,
            streetlightLatLng
          );
        return distance <= proximityThreshold;
      });

      if (nearbyStreetlights.length > 0) {
        // Find the nearest streetlight to the current point
        const nearestStreetlight = nearbyStreetlights.reduce((prev, curr) => {
          const prevLatLng = new window.google.maps.LatLng(
            parseFloat(prev.latitude),
            parseFloat(prev.longitude)
          );
          const currLatLng = new window.google.maps.LatLng(
            parseFloat(curr.latitude),
            parseFloat(curr.longitude)
          );
          const prevDistance =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              currentPoint,
              prevLatLng
            );
          const currDistance =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              currentPoint,
              currLatLng
            );
          return prevDistance < currDistance ? prev : curr;
        });

        // Return the nearest streetlight as a waypoint
        return {
          location: {
            lat: parseFloat(nearestStreetlight.latitude),
            lng: parseFloat(nearestStreetlight.longitude),
          },
          stopover: false,
        };
      }
    }
    return null;
  };

  // Function to calculate the route
  const calculateRoute = () => {
    if (!origin || !destination) {
      alert("Please specify both origin and destination.");
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    const originLocation =
      typeof origin === "string"
        ? origin
        : new window.google.maps.LatLng(
            origin.location.lat,
            origin.location.lng
          );
    const destinationLocation =
      typeof destination === "string"
        ? destination
        : new window.google.maps.LatLng(
            destination.location.lat,
            destination.location.lng
          );

    const originalRequest = {
      origin: originLocation,
      destination: destinationLocation,
      travelMode: window.google.maps.TravelMode.WALKING,
      provideRouteAlternatives: true,
    };

    directionsService.route(
      originalRequest,
      (originalResult, originalStatus) => {
        if (originalStatus === window.google.maps.DirectionsStatus.OK) {
          // Calculate the shortest distance
          const shortestRoute = originalResult.routes[0];
          const shortestDistance = computeRouteDistance(shortestRoute);
          const maxAllowedDistance = shortestDistance * 3;

          if (detourPreference === "Distance First") {
            // Select the shortest route
            let minDistance = Infinity;
            let selectedRouteIndex = 0;
            originalResult.routes.forEach((route, index) => {
              const distance = computeRouteDistance(route);
              if (distance < minDistance) {
                minDistance = distance;
                selectedRouteIndex = index;
              }
            });
            const selectedRoute = originalResult.routes[selectedRouteIndex];

            // Compare with last route
            if (lastRoute && areRoutesEqual(selectedRoute, lastRoute)) {
              alert("Both Shortest and Well-Lit routes are the same.");
            }

            // Set the directions response
            setDirectionsResponse({
              ...originalResult,
              routes: [selectedRoute],
            });

            // Set route info (distance and duration)
            if (selectedRoute.legs && selectedRoute.legs.length > 0) {
              const routeDistance = selectedRoute.legs.reduce(
                (sum, leg) => sum + leg.distance.value,
                0
              );
              const routeDuration = selectedRoute.legs.reduce(
                (sum, leg) => sum + leg.duration.value,
                0
              );

              setRouteInfo({
                distance: `${(routeDistance / 1000).toFixed(2)} km`,
                duration: `${Math.floor(routeDuration / 60)} mins`,
              });

              // Update lastRoute
              setLastRoute(selectedRoute);
            } else {
              console.error("No legs found in the selected route.");
              alert("Unable to calculate route information. Please try again.");
            }
          } else if (detourPreference === "Streetlight First") {
            // Select well-lit routes by adding one optimal waypoint
            const waypoint = selectOptimalWaypoint(
              origin,
              destination,
              markersData
            );

            if (!waypoint) {
              alert(
                "No streetlights found near the predefined points on the route."
              );
              return;
            }

            // Create enhanced request with the waypoint
            const enhancedRequest = {
              origin: originLocation,
              destination: destinationLocation,
              travelMode: window.google.maps.TravelMode.WALKING,
              waypoints: [waypoint],
              optimizeWaypoints: false,
              provideRouteAlternatives: false,
            };

            directionsService.route(
              enhancedRequest,
              (enhancedResult, enhancedStatus) => {
                if (enhancedStatus === window.google.maps.DirectionsStatus.OK) {
                  const enhancedRoute = enhancedResult.routes[0];
                  const enhancedDistance = computeRouteDistance(enhancedRoute);

                  if (enhancedDistance > maxAllowedDistance) {
                    alert(
                      "The well-lit route exceeds the maximum allowed distance."
                    );
                    return;
                  }

                  // Compare with last route
                  if (lastRoute && areRoutesEqual(enhancedRoute, lastRoute)) {
                    alert("Both Shortest and Well-Lit routes are the same.");
                  }

                  // Set the directions response
                  setDirectionsResponse(enhancedResult);

                  // Store the selected waypoint
                  setSelectedWaypoint(waypoint.location);

                  // Set route info (distance and duration)
                  if (enhancedRoute.legs && enhancedRoute.legs.length > 0) {
                    const routeDistance = enhancedRoute.legs.reduce(
                      (sum, leg) => sum + leg.distance.value,
                      0
                    );
                    const routeDuration = enhancedRoute.legs.reduce(
                      (sum, leg) => sum + leg.duration.value,
                      0
                    );

                    setRouteInfo({
                      distance: `${(routeDistance / 1000).toFixed(2)} km`,
                      duration: `${Math.floor(routeDuration / 60)} mins`,
                    });

                    // Update lastRoute
                    setLastRoute(enhancedRoute);
                  } else {
                    console.error("No legs found in the selected route.");
                    alert(
                      "Unable to calculate route information. Please try again."
                    );
                  }
                } else {
                  console.error(
                    `Error fetching directions: ${enhancedStatus}`,
                    enhancedResult
                  );
                  alert(`Directions request failed due to: ${enhancedStatus}`);
                }
              }
            );

            return;
          }
        } else {
          console.error(
            `Error fetching directions: ${originalStatus}`,
            originalResult
          );
          alert(`Directions request failed due to: ${originalStatus}`);
        }
      }
    );
  };

  // Function to generate Google Maps link for sharing
  const generateGoogleMapsLink = () => {
    if (!directionsResponse) {
      alert("Please search for a route first.");
      return;
    }

    const originParam =
      typeof origin === "string"
        ? encodeURIComponent(origin)
        : `${origin.location.lat},${origin.location.lng}`;
    const destinationParam =
      typeof destination === "string"
        ? encodeURIComponent(destination)
        : `${destination.location.lat},${destination.location.lng}`;

    let url = `https://www.google.com/maps/dir/?api=1&origin=${originParam}&destination=${destinationParam}&travelmode=walking`;

    // Include the selected waypoint if it exists
    if (selectedWaypoint) {
      const waypointParam = `${selectedWaypoint.lat},${selectedWaypoint.lng}`;
      url += `&waypoints=${encodeURIComponent(waypointParam)}`;
    }

    // Inform the user about possible discrepancies
    alert(
      "Please note: Due to limitations in Google Maps, the route may differ from the one calculated in the application."
    );

    // Open the link in a new tab
    window.open(url, "_blank");
  };

  if (!isLoaded || !isDataLoaded) {
    return <LoadingOverlay />;
  }

  return (
    <div className="map-container">
      <div className="card input-card">
        <div className="origin-group">
          <Autocomplete
            onLoad={(autocomplete) => {
              originAutocompleteRef.current = autocomplete;
            }}
            onPlaceChanged={onOriginPlaceChanged}
            options={{
              componentRestrictions: { country: "AU" },
              bounds: new window.google.maps.LatLngBounds(
                new window.google.maps.LatLng(-39.1599, 140.9617),
                new window.google.maps.LatLng(-33.9806, 149.9764)
              ),
              strictBounds: true,
            }}
          >
            <div className="input-wrapper">
              <input
                type="text"
                placeholder="Start Point"
                className="input-field"
                value={
                  typeof origin === "string" ? origin : origin.address || ""
                }
                onChange={(e) => {
                  setOrigin(e.target.value);
                }}
                aria-label="Origin Input"
              />
              {((typeof origin === "string" && origin !== "") ||
                (typeof origin === "object" && origin.address)) && (
                <span
                  className="clear-input"
                  onClick={() => setOrigin("")}
                  aria-label="Clear Origin Input"
                >
                  ×
                </span>
              )}
            </div>
          </Autocomplete>
          <div className="button-group">
            <button
              className="control-button"
              onClick={() => {
                setClickMode("origin");
                setPinMode(true);
                setHintMessage("Click on the map to set the start point");
              }}
              aria-label="Set Origin on Map"
            >
              Set Start Point on Map
            </button>
            <button
              className="control-button"
              onClick={useCurrentLocation}
              aria-label="Use Current Location"
            >
              Use Current Location
            </button>
          </div>
        </div>
        {pinMode && <div className="hint-message">{hintMessage}</div>}

        <div className="destination-group">
          <Autocomplete
            onLoad={(autocomplete) => {
              destinationAutocompleteRef.current = autocomplete;
            }}
            onPlaceChanged={onDestinationPlaceChanged}
            options={{
              componentRestrictions: { country: "AU" },
              bounds: new window.google.maps.LatLngBounds(
                new window.google.maps.LatLng(-39.1599, 140.9617),
                new window.google.maps.LatLng(-33.9806, 149.9764)
              ),
              strictBounds: true,
            }}
          >
            <div className="input-wrapper">
              <input
                type="text"
                placeholder="Destination"
                className="input-field"
                value={
                  typeof destination === "string"
                    ? destination
                    : destination.address || ""
                }
                onChange={(e) => {
                  setDestination(e.target.value);
                }}
                aria-label="Destination Input"
              />
              {((typeof destination === "string" && destination !== "") ||
                (typeof destination === "object" && destination.address)) && (
                <span
                  className="clear-input"
                  onClick={() => setDestination("")}
                  aria-label="Clear Destination Input"
                >
                  ×
                </span>
              )}
            </div>
          </Autocomplete>
          <button
            className="control-button"
            onClick={() => {
              setClickMode("destination");
              setPinMode(true);
              setHintMessage("Click on the map to set the destination point");
            }}
            aria-label="Set Destination on Map"
          >
            Set Destination on Map
          </button>
        </div>
        {pinMode && <div className="hint-message">{hintMessage}</div>}

        <div className="route-preference">
          <label htmlFor="detourPreference">Route Preference:</label>
          <select
            id="detourPreference"
            className="detour-select"
            value={detourPreference}
            onChange={(e) => setDetourPreference(e.target.value)}
            aria-label="Route Preference Select"
          >
            <option value="Distance First">Shortest</option>
            <option value="Streetlight First">Well-Lit</option>
          </select>
        </div>

        <div className="checkbox-label">
          <input
            type="checkbox"
            className="checkbox-input"
            checked={showAllStreetlights}
            onChange={(e) => setShowAllStreetlights(e.target.checked)}
            aria-label="Show All Streetlights"
          />
          Show All Streetlights
        </div>

        <div className="action-buttons">
          <button
            className="control-button"
            onClick={calculateRoute}
            aria-label="Calculate Route"
          >
            Calculate Route
          </button>
          <button
            className="control-button share-button"
            onClick={generateGoogleMapsLink}
            aria-label="Share Route"
          >
            View On Google Maps
          </button>
        </div>

        <div>
          {directionsResponse && routeInfo.distance && routeInfo.duration && (
            <div className="route-info">
              <strong>Route Distance:</strong> {routeInfo.distance}
              <br />
              <strong>Estimated Duration:</strong> {routeInfo.duration}
            </div>
          )}

          {showQuizPrompt && (
            <QuizPrompt onClose={() => setShowQuizPrompt(false)} />
          )}
        </div>
      </div>

      <div className="card map-card">
        <GoogleMap
          mapContainerClassName="google-map"
          center={center}
          zoom={11}
          onLoad={onLoad}
          onClick={onMapClick}
          options={{
            draggableCursor: pinMode ? "crosshair" : "grab",
          }}
        >
          {directionsResponse && (
            <DirectionsRenderer directions={directionsResponse} />
          )}
          {showAllStreetlights &&
            markersData.map((markerData, index) => (
              <Marker
                key={index}
                position={{
                  lat: parseFloat(markerData.latitude),
                  lng: parseFloat(markerData.longitude),
                }}
                title={`Street Light ${index + 1}`}
                icon={{
                  url: StreetLightIcon,
                  scaledSize: new window.google.maps.Size(20, 20),
                }}
              />
            ))}
          {typeof origin !== "string" && origin.location && (
            <Marker position={origin.location} label="A" title="Origin" />
          )}
          {typeof destination !== "string" && destination.location && (
            <Marker
              position={destination.location}
              label="B"
              title="Destination"
            />
          )}
        </GoogleMap>
      </div>
    </div>
  );
}

export default Map;
